import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, MapPin, Code2 } from 'lucide-react';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const STATS = [
  { value: '2+',  label: 'Years building' },
  { value: '10+', label: 'Projects shipped' },
  { value: '5+',  label: 'OSS contributions' },
];

const CODE = `{
  "name":      "Krishna Srivastava",
  "role":      "Full Stack Developer",
  "location":  "BIT Jaipur, India",
  "currently": [
    "Interning @ Zoolarity Labs",
    "Building AI-powered apps",
    "Contributing to open source"
  ],
  "interests": [
    "Clean architecture",
    "Developer tooling",
    "Blockchain & agents"
  ],
  "available": true
}`;

export default function About() {
  return (
    <section
      id="about"
      className="relative w-full overflow-hidden bg-transparent py-10 lg:py-14"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 lg:px-14">

        {/* ── Section label ── */}
        <motion.p
          {...fade(0)}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-6"
        >
          &lt;/About&gt;
        </motion.p>

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10 lg:gap-14 items-start">

          {/* ════ LEFT: Text content ════ */}
          <div>

            {/* Name */}
            <motion.h1
              {...fade(0.05)}
              className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black text-black leading-tight tracking-tight"
            >
              Hey, I'm{' '}
              <span className="text-purple-600">Krishna</span>
              <span className="inline-block ml-2 align-middle text-2xl animate-wave">👋</span>
            </motion.h1>

            {/* Role */}
            <motion.p
              {...fade(0.1)}
              className="text-base font-bold text-black/50 mt-1 mb-5 tracking-tight"
            >
              Full Stack Developer
            </motion.p>

            {/* Meta pills */}
            <motion.div
              {...fade(0.15)}
              className="flex flex-wrap items-center gap-2 mb-7"
            >
              {[
                { icon: <MapPin size={10} />, text: 'BIT Jaipur, India' },
                { icon: <Code2 size={10} />,  text: 'CS · 2023 – 2027' },
              ].map(({ icon, text }) => (
                <span
                  key={text}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-black/12 rounded-full text-[10px] font-mono text-black/50 shadow-[1px_1px_0px_0px_rgba(0,0,0,0.1)]"
                >
                  {icon} {text}
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-200 rounded-full text-[10px] font-mono text-green-700">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Open to work
              </span>
            </motion.div>

            {/* Bio */}
            <motion.p
              {...fade(0.2)}
              className="text-gray-600 text-[14px] sm:text-[15px] leading-[1.75] mb-8 max-w-[520px]"
            >
              CS undergrad building full-stack apps that somehow work in production.
              React up front,{' '}
              <span className="text-black font-semibold">Node / FastAPI</span>{' '}
              in the back, and whatever cursed tech the problem demands. Lately deep
              in{' '}
              <span className="text-black font-semibold">AI agents</span>, RAG
              pipelines, and blockchain — because normal web dev wasn't complicated
              enough. Also an{' '}
              <span className="text-black font-semibold">open-source contributor</span>,
              which is a fancy way of saying I fix other people's bugs for free.
            </motion.p>

            {/* Stats row */}
            <motion.div
              {...fade(0.25)}
              className="grid grid-cols-3 gap-4 py-6 border-y border-black/8 mb-8"
            >
              {STATS.map(({ value, label }) => (
                <div key={label}>
                  <span className="block text-2xl font-black text-black leading-none">
                    {value}
                  </span>
                  <span className="block text-[10px] font-mono text-black/35 uppercase tracking-wider mt-1">
                    {label}
                  </span>
                </div>
              ))}
            </motion.div>

            {/* CTA buttons */}
            <motion.div {...fade(0.3)} className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() =>
                  document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-bold border-2 border-black rounded-xl shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150"
              >
                View Projects <ArrowRight size={14} />
              </button>
              <a
                href="https://drive.google.com/drive/folders/1kaW4Zo6zFGDmF-QH75L3SgVEjeBibVKM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-bold border-2 border-black rounded-xl shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150"
              >
                Download CV <Download size={14} />
              </a>
            </motion.div>

          </div>

          {/* ════ RIGHT: Code card ════ */}
          <motion.div
            {...fade(0.22)}
            className="hidden lg:flex flex-col gap-3"
          >
            {/* Fake code editor window */}
            <div className="bg-white border-2 border-black rounded-2xl shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#111] border-b-2 border-black">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                <span className="ml-auto text-white/30 font-mono text-[9px] tracking-wider">
                  krishna.json
                </span>
              </div>

              {/* Code body with syntax colouring */}
              <div className="p-5 text-[11.5px] font-mono leading-[1.8] bg-[#FEFEFE]">
                {CODE.split('\n').map((line, i) => {
                  // very simple inline syntax highlight
                  const highlighted = line
                    .replace(/("(?:[^"\\]|\\.)*")\s*:/g, '<span style="color:#7c3aed;font-weight:600">$1</span>:')
                    .replace(/:\s*("(?:[^"\\]|\\.)*")/g, ': <span style="color:#16a34a">$1</span>')
                    .replace(/:\s*(true|false)/g, ': <span style="color:#db2777">$1</span>');
                  return (
                    <div
                      key={i}
                      className="text-gray-700"
                      dangerouslySetInnerHTML={{ __html: highlighted }}
                    />
                  );
                })}
              </div>
            </div>

            {/* "Currently" status bar */}
            <div className="flex items-center gap-3 bg-white border-2 border-black rounded-xl px-4 py-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
              <p className="text-[11px] font-mono text-black/60 leading-snug">
                Currently interning @{' '}
                <span className="font-bold text-black">Zoolarity Labs</span>
                {' '}— building NestJS APIs
              </p>
            </div>

          </motion.div>

        </div>
      </div>

      {/* Wave keyframe */}
      <style>{`
        @keyframes wave {
          0%,100%{transform:rotate(0deg)}
          10%,30%{transform:rotate(-10deg)}
          20%{transform:rotate(12deg)}
          40%{transform:rotate(-4deg)}
          50%{transform:rotate(10deg)}
          60%{transform:rotate(-6deg)}
          70%{transform:rotate(8deg)}
          80%{transform:rotate(-2deg)}
          90%{transform:rotate(4deg)}
        }
        .animate-wave{animation:wave 2.5s ease-in-out infinite}
      `}</style>
    </section>
  );
}